<script setup lang="ts">
import AlertMessage from '@/components/ui/AlertMessage.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import BaseInput from '@/components/ui/BaseInput.vue'
import CaptchaField from '@/components/ui/CaptchaField.vue'
import { USERNAME_MAX_LENGTH } from '@/contracts/auth.contract'

/**
 * 登入表單的欄位本體。
 *
 * `/login` 那一頁與被閘門擋下時彈出的對話框共用這一份 ——
 * 兩邊的欄位、順序、驗證碼與送出鈕必須一模一樣，複製兩份遲早會漂移。
 *
 * 展示元件：狀態由 `useLoginForm` 提供，這裡只負責畫與往上拋。
 *
 * 送出鈕**留在 `<form>` 裡面**而不是交給對話框的 footer slot：
 * 在輸入框按 Enter 要能送出，靠的是表單裡存在一顆 `type="submit"`。
 */
defineProps<{
  submitting: boolean
  formError: string | null
  fieldErrors: Record<string, string>
  captchaSvg: string | null
  captchaLoading: boolean
  captchaFailed: boolean
}>()

defineEmits<{ submit: []; refreshCaptcha: [] }>()

const username = defineModel<string>('username', { required: true })
const password = defineModel<string>('password', { required: true })
const captchaAnswer = defineModel<string>('captchaAnswer', { required: true })
</script>

<template>
  <form class="auth__form" novalidate @submit.prevent="$emit('submit')">
    <AlertMessage v-if="formError !== null">{{ formError }}</AlertMessage>

    <BaseInput
      v-model="username"
      :label="$t('auth.username')"
      :placeholder="$t('auth.usernamePlaceholder')"
      :error="fieldErrors['username'] ?? null"
      autocomplete="username"
      :maxlength="USERNAME_MAX_LENGTH"
      :disabled="submitting"
    />

    <BaseInput
      v-model="password"
      :label="$t('auth.password')"
      :placeholder="$t('auth.passwordPlaceholder')"
      :error="fieldErrors['password'] ?? null"
      type="password"
      autocomplete="current-password"
      :disabled="submitting"
    />

    <CaptchaField
      v-model="captchaAnswer"
      :svg="captchaSvg"
      :loading="captchaLoading"
      :failed="captchaFailed"
      :error="fieldErrors['captchaAnswer'] ?? fieldErrors['captchaId'] ?? null"
      :disabled="submitting"
      @refresh="$emit('refreshCaptcha')"
    />

    <BaseButton type="submit" block :loading="submitting">
      {{ submitting ? $t('auth.signingIn') : $t('auth.signIn') }}
    </BaseButton>
  </form>
</template>

<style scoped lang="scss">
/**
 * 只取 `_auth-form.scss` 的表單間距。
 *
 * 卡片外框（`.auth`）留給各自的容器 —— 對話框已經有自己的面板，
 * 再套一層外框會變成框中框。
 */
.auth__form {
  display: flex;
  flex-direction: column;
  gap: $space-4;
}
</style>
